<template>
  <div
    class="be-landing w-100 bg-white"
    :class="{ 'be-v3-content-font-default': !hasBeV3ContentFont }"
    :style="beV3StyleVars"
  >
    <div id="search-room"></div>
    <BeLandingNavbar />
    <div class="be-landing__spacer"></div>

    <!-- MERCHANT INFO + GALLERY + INTRO -->
    <div class="ga-container fluid pt-5 be-landing__intro">
      <div class="mb-4 pb-3">
        <BeMerchantHeaderInfo />
      </div>
      <div class="be-landing__section-gap be-landing__gallery">
        <BeMerchantGallery />
      </div>
      <div class="be-landing__section-gap">
        <BeMerchantIntro :starting-price="startingPrice" @check-availability="scrollToStay" />
      </div>
    </div>

    <!-- CHOOSE YOUR STAY + ROOM LIST -->
    <div class="ga-container fluid be-landing__section-gap bg-white position-relative">
      <div id="choose-your-stay" class="position-absolute" style="top: -90px; left: 0"></div>
      <div id="search-room-middle" class="position-absolute" style="top: -130px; left: 0"></div>

      <BeChooseYourStay class="mb-5" @search="loadRoomListFromFilter" />

      <div class="row mb-5">
        <div class="col-12 col-lg-9 order-2 order-lg-1">
          <div class="w-100 position-relativei">
            <div id="search-room-result" class="position-absolute" style="top: -420px; left: 0" />
          </div>

          <BeRoomFilterBar
            v-if="showRoomList"
            :total-room-result="totalRoomResult"
            :show-result-filters="!isMapModeActive"
            @filter-change="loadRoomList()"
            @clear-detail="clearFilterDetail"
            @clear-promotion="clearPromotionFilter" />

          <div v-if="showRoomList && hasDigitalMap" class="d-flex justify-content-end mb-3">
            <div
              class="text-center py-2 rounded-4 px-3 d-inline-block cursor-pointer"
              :class="{ 'border-green': view_mode == 'list' }"
              @click="toggleViewMode('list')"
              style="border: 1px solid #dee2e6">
              <p
                class="body-2-2 mb-0 cursor-pointer delay font-semibold rounded-4 text-14"
                :class="{ 'font-bold text-green': view_mode == 'list' }">
                {{ $t('hotel.map-toggle-list') }}
              </p>
            </div>
            <div
              class="text-center py-2 rounded-4 ml-2 px-3 d-inline-block cursor-pointer"
              :class="{ 'border-green': view_mode == 'map' }"
              @click="toggleViewMode('map')"
              style="border: 1px solid #dee2e6">
              <p
                class="body-2-2 mb-0 cursor-pointer delay font-semibold rounded-4 text-14"
                :class="{ 'font-bold text-green': view_mode == 'map' }">
                {{ $t('hotel.map-toggle-map') }}
              </p>
            </div>
          </div>

          <transition name="be-swap" mode="out-in">
            <div class="w-100" :key="activeModuleKey">
              <AdditionalModule v-if="!showRoomList" layout="v3" />

              <div class="w-100" v-else>
                <transition name="be-swap" mode="out-in">
                  <!-- A merchant with a booking map gets the map INSTEAD of the
                       list, and each popup runs its own per-room-type search on
                       top of the page's. -->
                  <BeDigitalMap
                    v-if="showMapView"
                    key="map"
                    :maps="rootMapList"
                    :merchant-id="getMerchant.id"
                    :selected-map-id.sync="active_root_map_id"
                    @starting-price="map_starting_price = $event"
                    :is-searching="room.isLoading"
                    @select-room-type="openMapRoomType" />

                  <b-skeleton-wrapper
                    v-else
                    key="list"
                    :loading="room.isLoading || is_map_checking">
                    <template #loading>
                      <BeRoomCardSkeleton />
                    </template>

                    <div class="w-100">
                      <template v-if="room.list.length == 0">
                        <div class="text-center py-4 mb-2 be-anim-fade-up">
                          <BeMembershipPromoLoginBanner class="mb-3" @logged-in="loadRoomList()" />
                          <FlexibleDateAlternatives
                            @dates-loaded="flexible_dates = $event; flexible_dates_loading = false"
                            @select-date="applyFlexibleDate" />
                          <template v-if="!flexible_dates_loading && flexible_dates.length == 0">
                            <img src="../../../assets/img/icon/404.png" width="200" />
                            <template v-if="$isNotEmptyV2(room.error)">
                              <p class="mb-1 mt-3 body-2-2 text-22 font-bold">{{ room.error }}</p>
                              <p class="mb-1 mt-3 body-2-2 text-22 font-bold">{{ $t('hotel.error.404') }}</p>
                            </template>
                            <p class="mb-3 body-2-2 font-semibold">{{ $t('hotel.error.404-desc') }}</p>
                          </template>
                          <button
                            class="button-primary be-v3-button-radius bg-white px-4 text-black mt-3 be-press"
                            @click="resetAllFilter()">
                            {{ $t('hotel.filter.reset-filter') }}
                          </button>
                        </div>
                      </template>

                      <div class="w-100" v-else>
                        <FlexibleDateAlternatives
                          v-if="isAllRoomsSoldOut"
                          class="mb-3"
                          @dates-loaded="flexible_dates = $event; flexible_dates_loading = false"
                          @select-date="applyFlexibleDate" />
                        <div class="w-100">
                          <BeRoomCard
                            v-for="card in roomCards"
                            :key="card.key"
                            class="be-anim-fade-up"
                            :style="{ animationDelay: card.delay }"
                            @room_type_detail="toggleRoomTypeDetail"
                            @toggleRateBreakdown="toggleRateBreakdown"
                            @toggleBookingRateCart="toggleBookingRateCart"
                            @handlePopupFilter="toggleBackdrop"
                            :type="card.type" />
                        </div>
                      </div>
                    </div>
                  </b-skeleton-wrapper>
                </transition>
              </div>
            </div>
          </transition>
        </div>

        <div class="col-12 col-lg-3 order-1 order-lg-2 position-relative">
          <div v-if="$isNotEmptyV2(publicPromoCodeWithBanner)" class="w-100 mb-4">
            <flickity ref="flickity" :options="$fullSliderOption">
              <div
                class="carousel-cell w-100"
                :id="promoCodeBanner.id"
                v-for="promoCodeBanner in publicPromoCodeWithBanner">
                <div class="w-100 be-v3-radius overflow-hidden" style="height: 200px">
                  <img
                    :src="promoCodeBanner.popup_image"
                    @click="selectPromoCode(promoCodeBanner)"
                    class="img-fit" />
                </div>
              </div>
            </flickity>
          </div>
          <BePotentialPromotion v-if="getMerchant.search_room_is_show_promotion_highlight" />
          <div class="preview-cart-desktop">
            <BeReservationSummary module="ADDITIONAL" />
          </div>
          <ClaimingReward wrapper_class="mt-5" />
        </div>
      </div>
    </div>

    <div class="ga-container fluid be-landing__section-gap">
      <BeAccommodationPolicy />
    </div>

    <div class="ga-container fluid be-landing__section-gap">
      <BeFaqSection />
    </div>

    <div class="ga-container fluid be-landing__section-gap">
      <BeLocationNearby />
    </div>

    <BeFooter />

    <!-- ROOM TYPE PICKED FROM THE DIGITAL MAP -->
    <BeMapRoomModal
      :room-type-id="mapRoomTypeId"
      :room-type-name="mapRoomTypeName"
      :search-params="map_search_params"
      @close="closeMapRoomType"
      @select-date="applyMapFlexibleDate"
      @room_type_detail="toggleRoomTypeDetail"
      @toggleRateBreakdown="toggleRateBreakdown"
      @toggleBookingRateCart="toggleBookingRateCart"
      @handlePopupFilter="toggleBackdrop" />

    <!-- ROOM TYPE DETAIL -->
    <transition name="be-modal">
      <roomTypeDetailDesktop
        v-if="$isNotEmptyV2(roomTypeDetail) && !$is_mobile_view"
        @detailRoom="toggleRoomTypeDetail()"
        @handlePopupFilter="toggleBackdrop"
        :detail_room_type="roomTypeDetail" />
    </transition>

    <transition name="be-sheet">
      <roomTypeDetailMobile
        v-if="$isNotEmptyV2(roomTypeDetail) && $is_mobile_view"
        @closeDetailRoom="toggleRoomTypeDetail()"
        :data="roomTypeDetail" />
    </transition>

    <transition name="be-sheet">
      <roomRateDetail
        v-if="$isNotEmptyV2(roomRateDetail) && mobilePopupType === 'room_rate' && $is_mobile_view"
        :more_room_form="showMoreRoomSelection"
        :show-more-room-only="getMerchant.search_room_is_show_room_rate_detail"
        :data="roomRateDetail"
        @close="toggleRoomRateDetail" />
    </transition>

    <!-- General Backdrop -->
    <transition name="fade">
      <PopUpBackground
        v-if="isBackdropActive"
        @handlePopupFilter="toggleBackdrop"
        :data="isBackdropActive"></PopUpBackground>
    </transition>

    <RoomCardConfirmModal
      stack-on-top
      v-model="showFilterChangeConfirm"
      title-key="hotel.search-filter-changed-title"
      desc-key="hotel.search-filter-changed-desc"
      confirm-key="hotel.search"
      @cancel="cancelFilterChangeSearch"
      @confirm="confirmFilterChangeSearch" />
  </div>
</template>

<script>
import { get } from '@/services/index.js';
import { withIndexedRateIds } from '@/utils/beRoomType.js';
import { resolveDefaultViewMode } from '@/utils/beViewMode.js';
import { mapGetters, mapMutations } from 'vuex';
import moment from 'moment';
import Flickity from 'vue-flickity';
import BeLandingNavbar from '@/components/hotel/be/v3/BeLandingNavbar.vue';
import BeMerchantHeaderInfo from '@/components/hotel/be/v3/BeMerchantHeaderInfo.vue';
import BeMerchantGallery from '@/components/hotel/be/v3/BeMerchantGallery.vue';
import BeMerchantIntro from '@/components/hotel/be/v3/BeMerchantIntro.vue';
import BeChooseYourStay from '@/components/hotel/be/v3/BeChooseYourStay.vue';
import BeRoomCard from '@/components/hotel/be/v3/BeRoomCard.vue';
import BeRoomCardSkeleton from '@/components/hotel/be/v3/BeRoomCardSkeleton.vue';
import BeRoomFilterBar from '@/components/hotel/be/v3/BeRoomFilterBar.vue';
import BeDigitalMap from '@/components/hotel/be/v3/BeDigitalMap.vue';
import BeMapRoomModal from '@/components/hotel/be/v3/BeMapRoomModal.vue';
import {
  pushHistoryStep,
  releaseHistoryStep,
  abandonHistorySteps,
} from '@/utils/historyStep.js';
import RoomCardConfirmModal from '@/components/hotel/be/v3/room-card/RoomCardConfirmModal.vue';
import AdditionalModule from '@/components/hotel/be/AdditionalModule.vue';
import BeReservationSummary from '@/components/hotel/be/v3/BeReservationSummary.vue';
import BePotentialPromotion from '@/components/hotel/be/v3/BePotentialPromotion.vue';
import BeAccommodationPolicy from '@/components/hotel/be/v3/BeAccommodationPolicy.vue';
import BeFaqSection from '@/components/hotel/be/v3/BeFaqSection.vue';
import BeLocationNearby from '@/components/hotel/be/v3/BeLocationNearby.vue';
import FlexibleDateAlternatives from '@/components/hotel/be/FlexibleDateAlternatives.vue';
import BeMembershipPromoLoginBanner from '@/components/hotel/be/BeMembershipPromoLoginBanner.vue';
import ClaimingReward from '@/components/hotel/general/ClaimingReward.vue';
import BeFooter from '@/components/hotel/be/v3/BeFooter.vue';
import PopUpBackground from '@/components/hotel/be/PopUpBackgroundDesktop.vue';
import roomTypeDetailDesktop from '@/components/hotel/be/desktop/roomTypeDetail';
import roomTypeDetailMobile from '@/components/hotel/be/mobile/roomTypeDetail';
import roomRateDetail from '@/components/hotel/be/mobile/roomRateDetail';
import { trackEvent } from '@/utils/analytics';
import { isSameBeFilter } from '@/utils/index.js';
import { resolveUsablePromoCode } from '@/utils/bePromoCode.js';
import { withPromotionFilter } from '@/utils/bePromotionFilter.js';

const BE_V3_FONT_STYLE_ID = 'be-v3-custom-fonts';
const BE_V3_SCROLLBAR_STYLE_ID = 'be-v3-hide-scrollbar';
const BE_V3_TITLE_FONT_FAMILY = 'BeV3TitleFont';
const BE_V3_CONTENT_FONT_FAMILY = 'BeV3ContentFont';
const BE_V3_TITLE_FONT_DEFAULT_FAMILY = 'BeV3TitleFontDefault';
const BE_V3_CONTENT_FONT_DEFAULT_FAMILY = 'BeV3ContentFontDefault';

/* Room cards fade in one after another; the delay stops growing after a few cards
   so a long result list never feels slower than a short one. */
const STAGGER_STEP_MS = 60;
const STAGGER_MAX_ITEMS = 6;

export default {
  name: 'SearchRoomLandingPage',
  props: ['promoCodePopup'],
  components: {
    Flickity,
    BeLandingNavbar,
    BeMerchantHeaderInfo,
    BeMerchantGallery,
    BeMerchantIntro,
    BeChooseYourStay,
    BeRoomCard,
    BeRoomCardSkeleton,
    BeRoomFilterBar,
    BeDigitalMap,
    BeMapRoomModal,
    RoomCardConfirmModal,
    AdditionalModule,
    BeReservationSummary,
    BePotentialPromotion,
    BeAccommodationPolicy,
    BeFaqSection,
    BeLocationNearby,
    FlexibleDateAlternatives,
    BeMembershipPromoLoginBanner,
    ClaimingReward,
    BeFooter,
    PopUpBackground,
    roomTypeDetailDesktop,
    roomTypeDetailMobile,
    roomRateDetail,
  },
  data() {
    return {
      room: {
        isLoading: true,
        error: '',
        list: [],
        typeAvailabilty: [],
      },
      isBackdropActive: false,
      roomTypeDetail: {},
      roomRateDetail: {},
      show_rate: false,
      show_rate_breakdown: false,
      is_booking_dropdown: false,
      is_booking_rate_cart: [],
      showMoreRoomSelection: false,
      mobilePopupType: '',
      flexible_dates: [],
      flexible_dates_loading: true,
      lastSearchedFilter: null,
      showFilterChangeConfirm: false,
      pendingSearch: null,
      view_mode: 'list',
      // set once loadRoomList runs at least once -- lets toggleViewMode skip
      // a redundant search on repeated list<->map switches
      hasSearchedRoomList: false,
      map_list: [],
      // true until the map list answers -- the page cannot know whether to show
      // the map or run a search before then, so neither is rendered yet
      is_map_checking: true,
      // held here rather than inside BeDigitalMap so the choice survives that
      // component unmounting
      active_root_map_id: null,
      // { id, name } from the clicked pin; the modal loads its own rates
      map_room_type_ref: null,
      map_search_params: {},
      // cheapest of the room types the map hydrated -- in map mode there is no
      // room list for the hero's "starting from" price to come from
      map_starting_price: 0,
    };
  },
  computed: {
    ...mapGetters([
      'getMerchant',
      'iActiveHotelModuleRoom',
      'getBeFilter',
      'getBeCartRoom',
      'getBeSortBy',
      'getBePrice',
      'getCurrentLanguage',
      'getMembershipReferral',
      'getMembershipRewardApplied',
      'isNotLogin',
      'getBeFilterDetail',
      'getBeRoomAvbl',
      'getSelectedPromotion',
      'getBePromotionFilterId',
      'getCurrentUser',
      'getActiveHotelModule',
    ]),
    // BeStayTabs only offers non-room tabs on SEARCH_ROOM merchants, so anywhere
    // else an active module is stale state and the room list stays.
    showRoomList() {
      return (
        this.iActiveHotelModuleRoom ||
        this.getMerchant.additional_appearance !== 'SEARCH_ROOM'
      );
    },
    rootMapList() {
      const roots = this.map_list.filter((map) => map.is_root);
      // is_root is not filled in consistently across merchants -- when nothing
      // is flagged, every map the list endpoint returns is a top-level one
      return roots.length > 0 ? roots : this.map_list;
    },
    hasDigitalMap() {
      return this.rootMapList.length > 0;
    },
    // hasDigitalMap alone no longer decides the active view -- view_mode
    // (seeded from config, movable via the toggle) does
    isMapModeActive() {
      return this.hasDigitalMap && this.view_mode == 'map';
    },
    showMapView() {
      return this.showRoomList && this.isMapModeActive;
    },
    mapRoomTypeId() {
      return this.map_room_type_ref ? this.map_room_type_ref.id : null;
    },
    mapRoomTypeName() {
      return this.map_room_type_ref ? this.map_room_type_ref.name : '';
    },
    activeModuleKey() {
      if (this.showRoomList) return 'room';
      const activeModule = this.getActiveHotelModule || {};
      return activeModule.id || 'module';
    },
    roomCards() {
      return this.room.list.map((type, index) => ({
        type: type,
        key: type.id,
        delay: `${Math.min(index, STAGGER_MAX_ITEMS) * STAGGER_STEP_MS}ms`,
      }));
    },
    hasBeV3TitleFont() {
      return this.$isNotEmptyV2(this.getMerchant.booking_engine_title_font);
    },
    hasBeV3ContentFont() {
      return this.$isNotEmptyV2(this.getMerchant.booking_engine_content_font);
    },
    beV3StyleVars() {
      const {
        booking_engine_border_radius: borderRadius,
        booking_engine_button_border_radius: buttonBorderRadius,
      } = this.getMerchant;

      const styleVars = {
        '--be-v3-title-font': this.hasBeV3TitleFont
          ? BE_V3_TITLE_FONT_FAMILY
          : BE_V3_TITLE_FONT_DEFAULT_FAMILY,
        '--be-v3-content-font': this.hasBeV3ContentFont
          ? BE_V3_CONTENT_FONT_FAMILY
          : BE_V3_CONTENT_FONT_DEFAULT_FAMILY,
      };
      if (this.$isNotEmptyV2(borderRadius)) styleVars['--be-v3-radius'] = borderRadius;
      if (this.$isNotEmptyV2(buttonBorderRadius))
        styleVars['--be-v3-button-radius'] = buttonBorderRadius;

      return styleVars;
    },
    startingPrice() {
      // map mode never runs a page-wide search, so the price comes from the
      // room types the map hydrated instead of from the room list
      if (this.isMapModeActive) return this.map_starting_price;
      if (!this.room.list || this.room.list.length == 0) return 0;
      let min = 0;
      this.room.list.forEach((type) => {
        const rates = type.room_rate || [];
        rates.forEach((rate) => {
          if (!rate.bookable) return;
          const dates = rate.dates || [];
          const total = dates.reduce((acc, item) => acc + (item.final_price || 0), 0);
          if (total > 0 && (min == 0 || total < min)) min = total;
        });
      });
      return min;
    },
    isAllRoomsSoldOut() {
      if (!this.room.list || this.room.list.length == 0) return false;
      return this.room.list.every((type) =>
        type.room_rate.every(
          (rate) => !rate.bookable || this.$roomAvbl(rate.room_type.id, rate.id) <= 0
        )
      );
    },
    showSpecificFilter() {
      return this.$isNotEmptyV2(this.getBeFilterDetail.data);
    },
    roomRateHighlightIndex() {
      return this.room.list.findIndex((room) => {
        const match = this.getBeRoomAvbl.find((r) => r.id === room.id);
        return match ? match.original_availability > 0 : false;
      });
    },
    roomRateHighlight() {
      if (!this.room.list) return { roomTypeIndex: -1, roomRateIndex: -1 };

      let roomRateIndex = -1;

      const roomTypeIndex = this.room.list.findIndex((room) => {
        const availableRoom = this.getBeRoomAvbl.find((avbl) => avbl.id === room.id);
        const isAvailable = availableRoom ? availableRoom.original_availability > 0 : false;

        if (!isAvailable || roomRateIndex !== -1) return false;

        const index = room.room_rate.findIndex((rate) => rate.bookable);
        const hasBookableRate = index !== -1;

        if (hasBookableRate) {
          roomRateIndex = index;
          return true;
        }

        return false;
      });

      return { roomTypeIndex, roomRateIndex };
    },
    publicPromoCodeWithBanner() {
      if (this.$isEmpty(this.promoCodePopup)) return [];
      return this.promoCodePopup.filter((promo) => this.$isNotEmptyV2(promo.popup_image));
    },
    totalRoomResult() {
      if (this.$isEmpty(this.room.list)) return 'Find the best option';

      // stop-sell and closed rates still come back from search-room, but they are not offers
      const roomRate = this.room.list.reduce((accumulation, currentValue) => {
        return accumulation + currentValue.room_rate.filter((rate) => rate.bookable).length;
      }, 0);

      return `${roomRate} ${this.$pluralWord(roomRate, 'room offer')}`;
    },
    selectedPromotion() {
      const promotion = this.getSelectedPromotion;
      if (!promotion) return '';
      return promotion.id;
    },
    beFilterDateSignature() {
      const { check_in, check_out } = this.getBeFilter.date;
      return `${this.$formatDateApi(check_in)}_${this.$formatDateApi(check_out)}`;
    },
  },
  watch: {
    /* An open popup was given the params of the dates it opened on. Rebuilding
       them here rather than in the search flow keeps the popup right whichever
       control moved the dates -- the carousel inside it, or the page's own
       search bar. */
    beFilterDateSignature() {
      if (this.map_room_type_ref) this.map_search_params = this.getParamsRoomList(false);
    },
    selectedPromotion() {
      // in map mode there is no list to refresh -- the popup picks the new
      // promotion up on its next open, when openMapRoomType rebuilds the params
      if (this.isMapModeActive) {
        this.closeMapRoomType();
        return;
      }
      if (!this.room.isLoading) {
        this.scrollToLoadedRoom();
        const validatePromotion = this.$isNotEmptyV2(this.selectedPromotion);
        this.loadRoomList(validatePromotion);
      }
    },
  },
  created() {
    /* Plain instance properties: a handle on the browser entry that mirrors an
       open layer is bookkeeping, not view state. */
    this.map_room_type_step = null;
    this.room_type_detail_step = null;
    this.view_mode = resolveDefaultViewMode(this.getMerchant);
    this.resetExpiredFilterDate();
  },
  mounted() {
    this.setupBeV3CustomFonts();
    this.setupBeV3HiddenScrollbar();
    this.lastSearchedFilter = this.cloneFilter(this.getBeFilter);
    this.bootstrapResults();
  },
  beforeDestroy() {
    const existingStyle = document.getElementById(BE_V3_SCROLLBAR_STYLE_ID);
    if (existingStyle) existingStyle.remove();
    abandonHistorySteps();
  },
  methods: {
    ...mapMutations([
      'resetBeFilter',
      'resetSelectedPromotion',
      'resetBePromotionFilter',
      'setBeRoomAvbl',
      'setBeFilterDetail',
      'setSpecificBeFilter',
      'BeManipulateRoomRate',
    ]),
    resetExpiredFilterDate() {
      const { check_in, check_out } = this.getBeFilter.date;
      const today = moment().startOf('day');
      if (moment(check_in).isSameOrAfter(today, 'day')) return;

      const nights = Math.max(moment(check_out).diff(moment(check_in), 'days'), 1);
      this.setSpecificBeFilter([
        {
          key: 'date',
          value: {
            check_in: today.toDate(),
            check_out: moment(today).add(nights, 'd').toDate(),
          },
        },
      ]);
    },
    scrollToStay() {
      this.$smoothScroll('choose-your-stay');
    },
    getFontFormatFromUrl(fontUrl) {
      const extension = fontUrl.split('?')[0].split('.').pop().toLowerCase();
      const formats = { woff2: 'woff2', woff: 'woff', ttf: 'truetype', otf: 'opentype' };
      return formats[extension] || 'truetype';
    },
    buildBeV3FontFace(fontFamily, fontUrl) {
      const format = this.getFontFormatFromUrl(fontUrl);
      // a merchant font is a single file, so claim the whole weight range —
      // otherwise the face registers at 400 and the browser synthesises bold
      return `@font-face { font-family: '${fontFamily}'; src: url('${fontUrl}') format('${format}'); font-weight: 100 900; font-display: swap; }`;
    },
    setupBeV3CustomFonts() {
      const {
        booking_engine_title_font: titleFontUrl,
        booking_engine_content_font: contentFontUrl,
      } = this.getMerchant;

      const fontFaces = [];
      if (this.hasBeV3TitleFont)
        fontFaces.push(this.buildBeV3FontFace(BE_V3_TITLE_FONT_FAMILY, titleFontUrl));
      if (this.hasBeV3ContentFont)
        fontFaces.push(this.buildBeV3FontFace(BE_V3_CONTENT_FONT_FAMILY, contentFontUrl));

      const existingStyle = document.getElementById(BE_V3_FONT_STYLE_ID);
      if (existingStyle) existingStyle.remove();
      if (fontFaces.length == 0) return;

      const styleElement = document.createElement('style');
      styleElement.id = BE_V3_FONT_STYLE_ID;
      styleElement.appendChild(document.createTextNode(fontFaces.join('\n')));
      document.head.appendChild(styleElement);
    },
    setupBeV3HiddenScrollbar() {
      // reclaims the native scrollbar's reserved layout width on mobile, so
      // position-fixed, full-width elements (navbar, mobile reservation bar)
      // reach the true screen edge instead of stopping short of it — real
      // phones already do this via overlay scrollbars, this just matches
      // that behaviour in desktop browsers emulating a mobile viewport
      const css = `
        @media screen and (max-width: 767.98px) {
          html, body { scrollbar-width: none; -ms-overflow-style: none; }
          html::-webkit-scrollbar, body::-webkit-scrollbar { width: 0; height: 0; display: none; }
        }
      `;

      const existingStyle = document.getElementById(BE_V3_SCROLLBAR_STYLE_ID);
      if (existingStyle) existingStyle.remove();

      const styleElement = document.createElement('style');
      styleElement.id = BE_V3_SCROLLBAR_STYLE_ID;
      styleElement.appendChild(document.createTextNode(css));
      document.head.appendChild(styleElement);
    },
    scrollToLoadedRoom() {
      const selectedElement = `search-room-${this.$is_mobile_view ? 'result' : 'middle'}`;
      this.$smoothScroll(selectedElement);
    },
    loadRoomListFromFilter() {
      this.trackSearchClick();
      this.requestSearch(this.getBeFilter);
    },
    cloneFilter(filter) {
      return JSON.parse(JSON.stringify(filter));
    },
    buildDefaultFilter() {
      return {
        date: {
          check_in: new Date(),
          check_out: moment()
            .add(this.getMerchant.search_room_filter_default_total_night, 'd')
            .toDate(),
        },
        guest: {
          adult: this.getMerchant.search_room_filter_default_total_adult,
          child: 0,
          infant: 0,
        },
        promo_code: '',
      };
    },
    requestSearch(newFilter, commit = () => {}, options = {}) {
      const filterChanged = !isSameBeFilter(this.lastSearchedFilter, newFilter);
      const hasCartItems = this.getBeCartRoom.length > 0;

      if (!filterChanged) {
        commit();
        this.executeSearch(false, newFilter, options);
        return;
      }

      if (!hasCartItems) {
        commit();
        this.executeSearch(true, newFilter, options);
        return;
      }

      this.pendingSearch = { commit, newFilter, options };
      this.showFilterChangeConfirm = true;
    },
    confirmFilterChangeSearch() {
      this.showFilterChangeConfirm = false;
      if (!this.pendingSearch) return;
      const { commit, newFilter, options } = this.pendingSearch;
      this.pendingSearch = null;
      commit();
      this.executeSearch(true, newFilter, options);
    },
    cancelFilterChangeSearch() {
      this.showFilterChangeConfirm = false;
      this.pendingSearch = null;
    },
    executeSearch(shouldResetCart, newFilter, options = {}) {
      if (shouldResetCart) this.BeManipulateRoomRate('reset');
      this.lastSearchedFilter = this.cloneFilter(newFilter);
      this.scrollToLoadedRoom();
      /* Map mode reloads the list too, even though the map itself is
         filter-independent: it gives the Search button a real loading state to
         show over the map, refreshes availability in the store, and leaves the
         list warm for a later toggle. An open popup holds the params it was
         opened with, so close it rather than leave stale rates on screen --
         unless the search came from inside the popup itself (keepMapPopup),
         where the guest asked for this very room on other dates and the
         beFilterDateSignature watcher reloads it in place. */
      if (this.isMapModeActive && !options.keepMapPopup) this.closeMapRoomType();
      this.loadRoomList();
    },
    trackSearchClick() {
      this.$trackRoomSearch();
      if (!this.$hasGoogleTag()) return;
      this.$gtag('event', 'search', { method: 'Google' });
    },
    async loadRoomList(validateSelectedPromotion = false) {
      this.hasSearchedRoomList = true;
      try {
        this.room = {
          isLoading: true,
          error: '',
          list: [],
          typeAvailabilty: [],
        };
        this.flexible_dates = [];
        this.flexible_dates_loading = true;
        const apiParams = this.getParamsRoomList(validateSelectedPromotion);
        const apiResponse = await get('CORE_HOTEL', 'search-room', apiParams);
        const { success, message, data, room_type_availability } = apiResponse.data;

        if (!success) throw new Error(message);

        trackEvent('view_item_list', {
          params: apiParams,
          result: data,
        });
        this.parsedRoomData(data, room_type_availability);
        if (
          this.$isNotEmptyV2(data) &&
          this.$isNotEmptyV2(this.getMerchant.google_tag_manager_id)
        )
          this.sendUserGuestData(data[0], apiParams);
        this.$sendEventMetaConversionsAPI('Search');
      } catch (error) {
        this.room.error = error.message;
      } finally {
        this.room.isLoading = false;
      }
    },
    sendUserGuestData(bestRoom, filter) {
      const { check_in_date: checkIn, check_out_date: checkOut, adult, child, infant } = filter;
      const bestRoomRate = bestRoom.room_rate[0];
      const params = {
        checkIn,
        checkOut,
        adult,
        child,
        infant,
        room: bestRoom.name,
        roomRate: bestRoomRate.name,
        bestPrice: bestRoomRate.dates.reduce((acc, value) => acc + value.final_price, 0),
      };

      this.$sendHotelUserGuestDataLayer('availability', params);
    },
    getParamsRoomList(validateSelectedPromotion) {
      const promotion = this.getParamsRoomListPromotion(validateSelectedPromotion);
      const stayDate = this.getParamsRoomListStayDate();
      const guests = this.getParamsRoomListGuest();
      const price = this.getParamsRoomListPrice();
      const membership = this.getParamsRoomListMembership();
      const specificFilter = this.getParamsRoomListSpecificFilter();

      return {
        ...promotion,
        ...stayDate,
        ...guests,
        ...price,
        ...membership,
        ...specificFilter,
        merchant_id: this.getMerchant.id,
        sort_by: this.getBeSortBy,
        language_code: this.getCurrentLanguage,
        device: 'desktop',
      };
    },
    getParamsRoomListPromotion(validateSelectedPromotion) {
      let params = { use_promotion_combine: true };
      const {
        promo_code,
        date: { check_in: startDate, check_out: endDate },
      } = this.getBeFilter;

      if (!validateSelectedPromotion && this.$isNotEmptyV2(promo_code)) {
        params.promo_code = promo_code;
        this.resetSelectedPromotion();
      }

      const isPromoCodeEmpty = this.$isEmpty(params.promo_code);
      const isPromoCodeMatch =
        this.$isNotEmptyV2(this.getSelectedPromotion.promo_code) &&
        promo_code === this.getSelectedPromotion.promo_code;

      if (isPromoCodeEmpty || isPromoCodeMatch) {
        const selected = this.$getSelectedPotentialPromotion(
          { startDate, endDate },
          validateSelectedPromotion
        );

        if (this.$isNotEmptyV2(selected)) {
          if (selected.promotion_type === 'PROMO_CODE') params.promo_code = selected.promo_code;
          else params.promotion_affected = selected.id;

          if (selected.promotion_type === 'MEMBERSHIP') {
            const { id: rewardId } = selected.membership_reward.reward;
            params.membership_reward_id = rewardId;
          }
        }
      }

      return withPromotionFilter(params, this.getBePromotionFilterId);
    },
    getParamsRoomListStayDate() {
      const { check_in, check_out } = this.getBeFilter.date;

      return {
        check_in_date: this.$formatDateApi(check_in),
        check_out_date: this.$formatDateApi(check_out),
      };
    },
    getParamsRoomListGuest() {
      const {
        search_room_is_show_filter_pax_child: useChild,
        search_room_is_show_filter_pax_infant: useInfant,
      } = this.getMerchant;

      const { adult, child, infant } = this.getBeFilter.guest;

      let params = {
        adult,
        child: useChild ? child : 0,
        infant: useInfant ? infant : 0,
      };

      if (!this.isNotLogin) {
        params.user_id = this.getCurrentUser.id;
      }

      return params;
    },
    getParamsRoomListPrice() {
      const isPriceEmpty = (price) => this.$isEmpty(price) || price === 0;

      return {
        price_min: isPriceEmpty(this.getBePrice[0]) ? '' : this.getBePrice[0],
        price_max: isPriceEmpty(this.getBePrice[1]) ? '' : this.getBePrice[1],
      };
    },
    getParamsRoomListMembership() {
      if (this.$isNotEmptyV2(this.getMembershipReferral)) {
        const { membership_referral_code, membership_reward_id } = this.getMembershipReferral;
        return { membership_referral_code, membership_reward_id };
      } else if (!this.isNotLogin) {
        const { id } = this.getMembershipRewardApplied;
        if (this.$isNotEmptyV2(id)) {
          return { membership_reward_id: id };
        }
      }
      return {};
    },
    getParamsRoomListSpecificFilter() {
      if (!this.showSpecificFilter) return {};

      const {
        type,
        data: { id },
      } = this.getBeFilterDetail;
      return type === 'room_type' ? { room_type_id: id } : { rate_id: id };
    },
    parsedRoomData(rooms, roomAvailability) {
      this.room.list = rooms.map(withIndexedRateIds);
      this.room.typeAvailabilty = roomAvailability.map((roomType) => {
        roomType.original_availability = roomType.availability;
        return roomType;
      });
      this.setBeRoomAvbl(this.room.typeAvailabilty);
    },
    resetAllFilter() {
      const defaultFilter = this.buildDefaultFilter();
      this.requestSearch(defaultFilter, () => {
        this.resetBeFilter();
        this.resetSelectedPromotion();
        this.resetBePromotionFilter();
      });
    },
    getRoomAvlb(roomTypeId) {
      let roomAvlb = this.getBeRoomAvbl.find((room) => room.id == roomTypeId);
      return roomAvlb ? roomAvlb.original_availability : 0;
    },
    discountAccumulation(counter, base) {
      let display = ((base - counter) * 100) / base;
      return +parseFloat(display).toFixed(2);
    },
    totalRateBreakdown(data) {
      let total_price_breakdown = 0;
      for (let i = 0; i < data.length; i++) {
        total_price_breakdown += data[i].final_price;
      }
      return total_price_breakdown;
    },
    clearFilterDetail() {
      this.setBeFilterDetail({ type: 'room_type', data: {} });
      this.loadRoomList();
    },
    clearPromotionFilter() {
      this.resetBePromotionFilter();
      if (this.isMapModeActive) this.closeMapRoomType();
      this.loadRoomList();
    },
    selectPromoCode(promotion) {
      const { promo_code } = promotion;
      this.setSpecificBeFilter([{ key: 'promo_code', value: promo_code }]);
      this.loadRoomList();
    },
    async applyFlexibleDate(dates) {
      const newFilter = await this.buildFlexibleDateFilter(dates);
      this.requestSearch(newFilter, () => this.commitFlexibleDateFilter(newFilter));
    },
    // same search, but the map popup that raised it stays open on the room the
    // guest picked the dates for
    async applyMapFlexibleDate(dates) {
      const newFilter = await this.buildFlexibleDateFilter(dates);
      this.requestSearch(newFilter, () => this.commitFlexibleDateFilter(newFilter), {
        keepMapPopup: true,
      });
    },
    // a wrong code carried in from the website plugin would fail the suggested dates too
    async buildFlexibleDateFilter({ check_in_date, check_out_date }) {
      const newFilter = this.cloneFilter(this.getBeFilter);
      newFilter.date = { check_in: check_in_date, check_out: check_out_date };
      newFilter.promo_code = await resolveUsablePromoCode(this.getMerchant.id, newFilter.promo_code);
      return newFilter;
    },
    commitFlexibleDateFilter({ date, promo_code }) {
      this.setSpecificBeFilter([
        { key: 'date', value: date },
        { key: 'promo_code', value: promo_code },
      ]);
    },
    toggleBackdrop(show = false) {
      this.isBackdropActive = show;
      if (!show) {
        this.show_rate = false;
        this.show_rate_breakdown = false;
        this.is_booking_dropdown = false;
        this.mobilePopupType = '';
        this.showMoreRoomSelection = false;
      }
    },
    /* The map list decides which of the two result views the page is, so it has
       to answer before anything else runs -- a merchant with a map never calls
       search-room for the page at all. */
    async bootstrapResults() {
      await this.loadMapList();
      this.is_map_checking = false;
      // no map: a config default of 'map' has nothing to show, fall back
      if (!this.hasDigitalMap) this.view_mode = 'list';
      // map mode runs no search-room on bootstrap, so the initial loading state
      // must be released here or BeDigitalMap's overlay never clears
      if (this.isMapModeActive) {
        this.room.isLoading = false;
        return;
      }
      this.loadRoomList();
    },
    // loaded here rather than inside BeDigitalMap, which is only mounted once
    // we already know the merchant has at least one map
    loadMapList() {
      return get('CORE_HOTEL', 'merchant-booking-map-list', {
        merchant_id: this.getMerchant.id,
      })
        .then((response) => {
          const res = response.data;
          if (res.success && this.$isNotEmptyV2(res.data)) this.map_list = res.data;
        })
        // a merchant whose map lookup fails still deserves a bookable page,
        // so treat the failure as "no map" and fall through to the list
        .catch(() => {});
    },
    /* Params are snapshotted on click, not derived in a computed:
       getParamsRoomList reaches getParamsRoomListPromotion, which commits store
       mutations, and running that during render would loop. Opening the popup
       is the moment the guest asks for fresh data, so this is just as current. */
    openMapRoomType(roomTypeRef) {
      this.map_search_params = this.getParamsRoomList(false);
      this.map_room_type_ref = roomTypeRef;
      this.map_room_type_step = pushHistoryStep(this.dismissMapRoomType);
    },
    /* Browser Back: its history entry is already consumed, so drop the handle
       first and let the normal close path run without rewinding again. */
    dismissMapRoomType() {
      this.map_room_type_step = null;
      this.closeMapRoomType();
    },
    closeMapRoomType() {
      if (!this.map_room_type_ref) return;
      const step = this.map_room_type_step;
      this.map_room_type_step = null;
      this.map_room_type_ref = null;
      /* Rate Detail / Book Now inside the popup raise the page-level backdrop
         and leave show_rate* set. The popup owns the only row those flags refer
         to, so closing it has to clear them or the backdrop outlives it. */
      this.toggleBackdrop(false);
      // also rewinds past a room type detail stacked above, closing it too
      if (step) releaseHistoryStep(step);
    },
    // lazy-loads the room list the first time a MAP-default merchant's guest
    // switches to list, instead of always searching on page load
    toggleViewMode(mode) {
      this.view_mode = mode;
      if (mode == 'list' && !this.hasSearchedRoomList) {
        this.loadRoomList();
      }
    },
    toggleRoomTypeDetail(data = {}) {
      const isOpening = this.$isNotEmptyV2(data);
      this.roomTypeDetail = data;
      this.toggleBackdrop(isOpening);
      if (!isOpening) {
        this.releaseRoomTypeDetailStep();
        return;
      }
      /* Only while stacked on the map popup: reached from the plain room list
         this modal keeps its existing Back behaviour. */
      if (this.map_room_type_ref) {
        this.room_type_detail_step = pushHistoryStep(this.dismissRoomTypeDetail);
      }
    },
    dismissRoomTypeDetail() {
      this.room_type_detail_step = null;
      this.toggleRoomTypeDetail();
    },
    releaseRoomTypeDetailStep() {
      if (!this.room_type_detail_step) return;
      const step = this.room_type_detail_step;
      this.room_type_detail_step = null;
      releaseHistoryStep(step);
    },
    toggleRoomRateDetail(data = {}) {
      this.mobilePopupType = data == '' ? 'close' : 'room_rate';
      this.roomRateDetail = data;
      this.toggleBackdrop(this.$isNotEmptyV2(data) ? true : false);
    },
    toggleRoomRateAdd(data = {}) {
      this.mobilePopupType = data == '' ? 'close' : 'room_rate_add';
      this.roomRateDetail = data;
      this.toggleBackdrop(this.$isNotEmptyV2(data) ? true : false);
    },
    toggleRateBreakdown(type, data) {
      if (type == 'rates') {
        if (this.show_rate == data) {
          this.show_rate = false;
          return;
        }
        this.show_rate = data;
        this.toggleBackdrop(true);
      } else if (type == 'rates-breakdown') {
        if (this.show_rate_breakdown == data) {
          this.show_rate_breakdown = false;
          return;
        }

        this.show_rate_breakdown = data;
        this.show_rate = data;
        this.toggleBackdrop(true);
      }
    },
    toggleBookingRate(data) {
      if (this.is_booking_dropdown == data) {
        this.is_booking_dropdown = '';
        this.toggleBackdrop(false);
      } else {
        this.is_booking_dropdown = data;
        this.toggleBackdrop(true);
      }
    },
    toggleBookingRateCart(type, data, qty = 1) {
      let room_selected = data;
      if (type == 'add') {
        if (this.is_booking_rate_cart.length == 0) {
          room_selected['qty'] = qty;
          this.is_booking_rate_cart.push(room_selected);
        } else {
          let is_exist = false;
          for (let i = 0; i < this.is_booking_rate_cart.length; i++) {
            if (room_selected.id == this.is_booking_rate_cart[i].id) {
              is_exist = i;
              break;
            }
          }
          if (is_exist === false) {
            room_selected['qty'] = qty;
            this.is_booking_rate_cart.push(room_selected);
          } else {
            this.is_booking_rate_cart[is_exist].qty = qty;
          }
        }
      } else {
        this.is_booking_rate_cart.splice(data, 1);
      }
      this.is_booking_dropdown = false;
      this.show_rate = false;
    },
    openMoreRoomInput(data) {
      this.mobilePopupType = 'room_rate';
      this.showMoreRoomSelection = true;
      this.roomRateDetail = data;
    },
  },
};
</script>

<style scoped>
/* must match BeLandingNavbar's min-height — the navbar is position-fixed, so any
   shortfall here leaves the top of the page tucked underneath it */
.be-landing__spacer {
  height: 78px;
}
.be-landing__section-gap {
  margin-bottom: 4rem;
}
@media screen and (max-width: 767.98px) {
  /* the navbar floats over the hero gallery, so the page starts flush at the top
     and the gallery is lifted above the merchant name */
  .be-landing__spacer {
    height: 0;
  }
  .be-landing__intro {
    display: flex;
    flex-direction: column;
    padding-top: 0 !important;
  }
  .be-landing__gallery {
    order: -1;
    margin-bottom: 1.75rem;
  }
}
</style>
<style>
.be-landing {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
}
/* CSS owns this breakpoint rather than $is_tablet_lower_view, which is evaluated once
   at load and never on resize — the class stayed stale when crossing 992px */
@media screen and (min-width: 992px) {
  .be-landing .preview-cart-desktop {
    position: sticky;
    top: 100px;
    z-index: 1039 !important;
  }
}
.be-landing .be-v3-radius {
  border-radius: var(--be-v3-radius, 8px);
}
.be-landing .be-v3-button-radius {
  border-radius: var(--be-v3-button-radius, 8px) !important;
}
/* Global typography utilities hardcode a per-weight Nunito family with !important,
   which breaks out of the BE v3 font family. Re-point them to the content font.
   The :not() list holds off Bootstrap's own .modal-body / .card-body / .popover-body /
   .toast-body, which [class*='body-'] would otherwise catch. */
.be-landing [class*='body-']:not(.modal-body):not(.card-body):not(.popover-body):not(.toast-body),
.be-landing .font-regular {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif !important;
}
.be-landing .font-extrabold {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif !important;
  font-weight: 700;
}
.be-landing .font-bold {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif !important;
  font-weight: 600;
}
.be-landing .font-semibold {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif !important;
  font-weight: 400;
}
</style>



// WEBPACK FOOTER //
// src/pages/hotel/be/SearchRoomLandingPage.vue